import React, { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import { useProposal } from "../context/ProposalContext";
import Header from "../components/Header";
import StepIndicator from "../components/StepIndicator";
import Step1EventDetails from "../components/steps/Step1EventDetails";
import Step2CostEstimate from "../components/steps/Step2CostEstimate";
import Step3RecipientInfo from "../components/steps/Step3RecipientInfo";
import Step4Proposal from "../components/steps/Step4Proposal";
import { Card, CardContent } from "../components/ui/card";
import { Sparkles, FileCheck2, Clock, UserRound } from "lucide-react";

const STEP_MOTION = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -12 },
  transition: { duration: 0.25, ease: "easeOut" },
};

const HELP = {
  1: { icon: Sparkles, title: "Tip: Upload to auto-fill", text: "Drop a past proposal or governing document and our AI extracts the key parameters for you." },
  2: { icon: Clock, title: "Real-time estimate", text: "Costs recalculate instantly — edit quantities, unit prices, or add custom line items." },
  3: { icon: UserRound, title: "Recipient details", text: "Complete all fields so we can generate and send your proposal right away." },
  4: { icon: FileCheck2, title: "Client-ready output", text: "A brand-consistent PDF with cover letter, pricing and terms — downloaded automatically." },
};

export default function ProposalWizard() {
  const { state, dispatch } = useProposal();
  const [searchParams, setSearchParams] = useSearchParams();
  const step = state.step;
  const setStep = (s) => dispatch({ type: "SET_STEP", step: s });
  const reset = () => dispatch({ type: "RESET" });

  // Starting a brand-new proposal via the header link (/?new=1) clears any saved draft.
  useEffect(() => {
    if (searchParams.get("new") !== null) {
      dispatch({ type: "RESET" });
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, dispatch, setSearchParams]);

  const Help = HELP[step];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="mb-8">
          <h1 className="font-display font-800 text-4xl sm:text-5xl text-foreground tracking-tight">
            Build a proposal in minutes
          </h1>
          <p className="text-muted-foreground mt-2 max-w-xl">
            From event parameters to a polished, client-ready PDF — no spreadsheets, no manual formatting.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left column */}
          <aside className="lg:col-span-4 space-y-6">
            <Card className="bg-white">
              <CardContent className="p-6">
                <p className="font-display font-600 text-sm text-muted-foreground uppercase tracking-wider mb-5">
                  Progress
                </p>
                <StepIndicator current={step} />
              </CardContent>
            </Card>

            <Card className="bg-primary text-white border-primary overflow-hidden relative">
              <CardContent className="p-6">
                <Help.icon className="h-6 w-6 mb-3 opacity-90" />
                <p className="font-display font-700 text-base">{Help.title}</p>
                <p className="text-sm opacity-90 mt-1.5 leading-relaxed">{Help.text}</p>
              </CardContent>
            </Card>
          </aside>

          {/* Right column */}
          <section className="lg:col-span-8">
            <Card className="bg-white">
              <CardContent className="p-6 sm:p-10">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={step}
                    {...STEP_MOTION}
                  >
                    {step === 1 && <Step1EventDetails onNext={() => setStep(2)} />}
                    {step === 2 && (
                      <Step2CostEstimate onNext={() => setStep(3)} onBack={() => setStep(1)} />
                    )}
                    {step === 3 && (
                      <Step3RecipientInfo onNext={() => setStep(4)} onBack={() => setStep(2)} />
                    )}
                    {step === 4 && (
                      <Step4Proposal onBack={() => setStep(3)} onReset={reset} />
                    )}
                  </motion.div>
                </AnimatePresence>
              </CardContent>
            </Card>
          </section>
        </div>
      </main>
    </div>
  );
}
